import type { LanguageCode } from './languages';
import { toPhpLangId } from './php-langid';
import type { EnMessages, LocaleCode } from './types';
import userLocalesConfig from './user-locales-config.json';

/**
 * Compile-time check: every CountryCode must have a Country.inCountry
 * translation key, and vice versa. If you see a type error here, add
 * the missing country to Country.inCountry in Shared/en.json (or
 * remove the stale key).
 */
type InCountryKey = keyof EnMessages['Country']['inCountry'];
type MissingTranslation = Exclude<CountryCode, InCountryKey>;
type ExtraTranslation = Exclude<InCountryKey, CountryCode>;
type Assert<T extends never> = T;
type _CheckNoMissingTranslations = Assert<MissingTranslation>;
type _CheckNoExtraTranslations = Assert<ExtraTranslation>;

/**
 * A user locale encodes a language + country preference (e.g. "es-ES").
 * It's persisted as a cookie and resolved to the best available store locale.
 *
 * Derived from the config so every key in the locales dictionaries is a valid
 * user locale code.
 */
type Config = typeof userLocalesConfig;
type Countries = Config['countries'];
export type UserLocaleCode = {
   [C in keyof Countries]: keyof Countries[C]['locales'];
}[keyof Countries];

interface CountryConfig {
   area: string;
   currency: string;
   localName: string;
   englishName?: string;
   locales: Record<string, string>;
}

const countries = userLocalesConfig.countries as Record<string, CountryConfig>;

export type CountryCode = keyof typeof userLocalesConfig.countries;

export interface Country {
   code: CountryCode;
   localName: string;
   englishName?: string;
   locales: readonly UserLocaleCode[];
}

export interface CountryArea {
   area: string;
   countries: Country[];
}

/**
 * Flat lookup table mapping every user locale code to its route locale.
 */
export const routeLocaleByUserLocale = Object.fromEntries(
   Object.values(countries).flatMap(country => Object.entries(country.locales))
) as Record<UserLocaleCode, LocaleCode>;

export function isUserLocaleCode(code: string): code is UserLocaleCode {
   // Own-key check, not `in`: the cookie value is client-controlled, and `in`
   // accepts inherited keys like "toString", which crash route construction
   // later. The long form because carpenter's TS lib predates Object.hasOwn.
   return Object.prototype.hasOwnProperty.call(routeLocaleByUserLocale, code);
}

/**
 * Country list grouped by area, preserving config order.
 */
export const countriesByArea = buildCountryAreas();

/**
 * Flat lookup from country code to its entry.
 */
export const countryByCode = Object.fromEntries(
   countriesByArea.flatMap(g => g.countries).map(c => [c.code, c])
) as Record<CountryCode, Country>;

function buildCountryAreas(): CountryArea[] {
   const groups: CountryArea[] = [];
   const areaMap = new Map<string, Country[]>();

   for (const [code, config] of Object.entries(countries)) {
      let entries = areaMap.get(config.area);
      if (!entries) {
         entries = [];
         areaMap.set(config.area, entries);
         groups.push({ area: config.area, countries: entries });
      }
      entries.push({
         code: code as CountryCode,
         localName: config.localName,
         ...(config.englishName && { englishName: config.englishName }),
         locales: Object.keys(config.locales) as UserLocaleCode[],
      });
   }

   for (const group of groups) {
      group.countries.sort((a, b) => a.localName.localeCompare(b.localName));
   }

   return groups;
}

/**
 * Parse a user locale into language and country parts.
 */
export function parseUserLocale(userLocale: UserLocaleCode): {
   language: LanguageCode;
   country: CountryCode;
} {
   const [language, country] = userLocale.split('-');
   return { language: toPhpLangId(language) as LanguageCode, country: country as CountryCode };
}

/**
 * Default user locale for each route locale (first match in config order).
 * Used as fallback for first-time visitors who have no cookie yet.
 */
export const defaultUserLocaleByRouteLocale = Object.entries(routeLocaleByUserLocale).reduce(
   (map, [userLocale, routeLocale]) => {
      map[routeLocale] ??= userLocale as UserLocaleCode;
      return map;
   },
   {} as Partial<Record<LocaleCode, UserLocaleCode>>
);

/**
 * All user locales for each route locale, preserving config order.
 * Used to match PHP preferences (store + language) to a user locale.
 */
export const userLocalesByRouteLocale = Object.entries(routeLocaleByUserLocale).reduce(
   (map, [userLocale, routeLocale]) => {
      (map[routeLocale] ??= []).push(userLocale as UserLocaleCode);
      return map;
   },
   {} as Partial<Record<LocaleCode, UserLocaleCode[]>>
);
